import React, { useEffect, useState } from "react";
import { X, Baby } from "lucide-react";
import { useLang } from "../lib/i18n";

const KEY = "bf_child_profile";

export function getStoredProfile() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}

export default function ChildProfileModal({ open, onClose }) {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [mainEmotion, setMainEmotion] = useState("");

  useEffect(() => {
    if (open) {
      const p = getStoredProfile();
      if (p) { setName(p.name || ""); setAge(p.age || ""); setMainEmotion(p.main_emotion || ""); }
    }
  }, [open]);

  if (!open) return null;

  const emotions = lang === "fr"
    ? [["anger", "Colère"], ["anxiety", "Anxiété"], ["confidence", "Confiance"], ["fear", "Peur"], ["sadness", "Tristesse"]]
    : [["anger", "Anger"], ["anxiety", "Anxiety"], ["confidence", "Confidence"], ["fear", "Fear"], ["sadness", "Sadness"]];

  const save = (e) => {
    e.preventDefault();
    const p = { name: name.trim(), age: age ? Number(age) : null, main_emotion: mainEmotion };
    localStorage.setItem(KEY, JSON.stringify(p));
    window.dispatchEvent(new CustomEvent("bf-profile-changed"));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#3D405B]/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="profile-modal"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl relative"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#8A8D91]" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D405B] text-xs font-semibold uppercase tracking-wider">
          <Baby className="w-3.5 h-3.5" /> {t("profile.badge")}
        </div>
        <h2 className="font-heading text-2xl text-[#3D405B] mt-3">{t("profile.title")}</h2>
        <p className="text-[#8A8D91] text-sm mt-1">{t("profile.subtitle")}</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#3D405B]">{t("profile.name")}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="profile-name"
              placeholder={t("profile.name.placeholder")}
              className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-2.5 text-[#3D405B] focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#3D405B]">{t("profile.age")}</label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              data-testid="profile-age"
              min="0" max="18"
              className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-2.5 text-[#3D405B] focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#3D405B]">{t("profile.emotion")}</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {emotions.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMainEmotion(mainEmotion === id ? "" : id)}
                  data-testid={`profile-emotion-${id}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    mainEmotion === id ? "bg-[#3D405B] text-white border-[#3D405B]" : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#3D405B]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          type="submit"
          data-testid="profile-save"
          className="mt-6 w-full bg-[#E07A5F] text-white rounded-full py-3 font-medium hover:bg-[#D56D52] transition-colors"
        >
          {t("profile.save")}
        </button>
      </form>
    </div>
  );
}
